import { Card, CardContent, CardHeader, CardTitle } from './ui/card';

import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from './ui/accordion';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from './ui/table';
import DailyBreakdownTable from './daily-breakdown-table';
import { VirtualizedOrderTable } from './virtualized-order-table';
import {
  DollarSign,
  ShoppingCart,
  TrendingUp,
  TrendingDown,
  PieChart,
  Clock,
  CreditCard,
  Banknote,
  Smartphone,
  Users,
  Truck,
  Gift,
  Store,
  BarChart3,
  Percent,
  ListChecks,
} from 'lucide-react';
import {
  sortTenderTypes,
  sortReportData,
  getProviderDisplayName,
  getCardTypeDisplayName,
  getDiscountTypeDisplayName,
  getDeliveryProviderDisplayName,
} from './lib/ordering';
import { useState } from 'react';

export default function ReportOutput({ data }) {
  // Helper to get tip data with backward compatibility
  const getTipData = () => {
    // Use new tipReport structure if available, fallback to legacy tipSummary
    return data.tipReport || data.tipSummary;
  };

  const tipData = getTipData();

  // Helper to get tip summary data (handles both new 'summary' and legacy 'overview' keys)
  const getTipSummary = () => {
    if (!tipData) return null;
    return tipData.summary;
  };

  const tipSummary = getTipSummary();

  // Helper to get tip breakdown by payment type (handles both new and old field names)
  const getTipByPaymentType = () => {
    if (!tipData) return null;
    const tipPaymentData =
      tipData.breakdownByPaymentType || tipData.byPaymentType;
    if (!tipPaymentData) return null;

    // Sort by payment type using the same order as tender types (card, cash, others)
    return sortTenderTypes(
      tipPaymentData.map((tip) => ({
        ...tip,
        _id: tip.paymentType, // Map paymentType to _id for sorting
      }))
    ).map((tip) => ({
      ...tip,
      paymentType: tip._id, // Map back to paymentType
    }));
  };

  // Helper to get tip breakdown by source (handles both new and old field names)
  const getTipBySource = () => {
    if (!tipData) return null;
    return tipData.breakdownBySource || tipData.bySource;
  };

  // Helper to get customer data with backward compatibility
  const getCustomerData = () => {
    // Use new customerReport structure if available, fallback to legacy customersReport
    return data.customerReport || data.customersReport;
  };

  const customerData = getCustomerData();

  // State for toggling between money and order count view
  const [showOrderCounts, setShowOrderCounts] = useState(false);

  const formatCurrency = (amount) => {
    const normalized = Object.is(amount, -0) ? 0 : amount;
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
    }).format(normalized);
  };

  const formatDate = (dateString) => {
    // Parse the date string (YYYY-MM-DD format) without timezone conversion
    const [year, month, day] = dateString.split('-').map(Number);
    const date = new Date(year, month - 1, day); // month is 0-indexed

    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  };

  const formatDateTime = () => {
    return new Date().toLocaleString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      hour12: true,
    });
  };

  const getTenderIcon = (tenderType) => {
    const type = tenderType.toLowerCase();
    if (type.includes('card')) return <CreditCard className="h-4 w-4" />;
    if (type.includes('cash')) return <Banknote className="h-4 w-4" />;
    return <Smartphone className="h-4 w-4" />;
  };

  const getCardTypeIcon = (cardType) => {
    const type = cardType.toLowerCase();
    if (type.includes('visa'))
      return <CreditCard className="h-3 w-3 text-blue-600" />;
    if (type.includes('mastercard') || type.includes('master'))
      return <CreditCard className="h-3 w-3 text-red-600" />;
    if (type.includes('amex') || type.includes('american'))
      return <CreditCard className="h-3 w-3 text-green-600" />;
    if (type.includes('discover'))
      return <CreditCard className="h-3 w-3 text-orange-600" />;
    return <CreditCard className="h-3 w-3 text-gray-600" />;
  };

  // Apply global sorting to all data
  const sortedData = sortReportData(data);

  const getSourceIcon = (source) => {
    const src = source.toLowerCase();
    if (src === 'pos') return <Store className="h-4 w-4" />;
    return <Truck className="h-4 w-4" />;
  };

  return (
    <div className="space-y-8">
      {/* Business Header (Print Only) */}
      {data.businessInfo && (
        <div className="print-only hidden print:block print-card print-avoid-break">
          <div className="text-center border-b border-gray-300 pb-6 mb-6">
            <h1 className="text-3xl font-bold text-black mb-2">
              {data.businessInfo.businessTitle}
            </h1>
            <div className="text-sm text-gray-600 space-y-1">
              <p>{data.businessInfo.businessAddressInfo.fullAddress}</p>
              <p>
                {data.businessInfo.businessAddressInfo.city},{' '}
                {data.businessInfo.businessAddressInfo.state}{' '}
                {data.businessInfo.businessAddressInfo.zipCode}
              </p>
              <p>Phone: {data.businessInfo.businessAdminPhone}</p>
              <p>
                Contact: {data.businessInfo.businessAdminFirstName}{' '}
                {data.businessInfo.businessAdminLastName}
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Report Header */}
      <Card className="print-card print-avoid-break">
        <CardContent className="pt-6">
          <div className="border-b border-border pb-4 mb-4">
            {/* Show business info on screen when available */}
            {data.businessInfo && (
              <div className="print:hidden mb-4 p-4 bg-muted rounded-lg">
                <div className="flex items-start justify-between">
                  <div>
                    <h3 className="text-lg font-semibold text-foreground mb-1">
                      {data.businessInfo.businessTitle}
                    </h3>
                    <p className="text-sm text-muted-foreground">
                      {data.businessInfo.businessAddressInfo.city},{' '}
                      {data.businessInfo.businessAddressInfo.state}
                    </p>
                  </div>
                  <div className="text-right text-sm text-muted-foreground">
                    <p>
                      {data.businessInfo.businessAdminFirstName}{' '}
                      {data.businessInfo.businessAdminLastName}
                    </p>
                    <p>{data.businessInfo.businessAdminPhone}</p>
                  </div>
                </div>
              </div>
            )}

            <h2 className="text-2xl font-bold text-foreground print-title">
              POS Performance Report
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4 text-sm text-muted-foreground">
              <div>
                <span className="font-medium">Report Period:</span>
                <span className="ml-2" data-testid="text-date-range">
                  {formatDate(data.dateRange.startDate)} -{' '}
                  {formatDate(data.dateRange.endDate)}
                </span>
              </div>
              <div>
                <span className="font-medium">Generated:</span>
                <span className="ml-2" data-testid="text-generated-at">
                  {formatDateTime()}
                </span>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Sales Summary */}
      <Card className="print-card">
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle className="flex items-center">
              <BarChart3 className="text-primary mr-3 h-5 w-5" />
              Sales Summary
            </CardTitle>
            {/* Temporarily disabled Money/Order toggle */}
            {/* <div className="flex items-center space-x-2">
              <Label htmlFor="view-toggle" className="text-sm font-medium">
                {showOrderCounts ? "Orders" : "Money"}
              </Label>
              <Switch
                id="view-toggle"
                checked={showOrderCounts}
                onCheckedChange={setShowOrderCounts}
              />
            </div> */}
          </div>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            <div className="flex items-center justify-between p-4 bg-muted rounded-lg">
              <div>
                <p className="text-sm font-medium text-muted-foreground">
                  {showOrderCounts ? 'Total Orders' : 'Total Revenue'}
                </p>
                <p
                  className="text-3xl font-bold text-foreground"
                  data-testid={
                    showOrderCounts ? 'text-total-orders' : 'text-total-revenue'
                  }
                >
                  {showOrderCounts
                    ? (
                        sortedData.moneyReport?.summary?.totalOrders || 0
                      ).toLocaleString()
                    : formatCurrency(
                        sortedData.moneyReport?.summary?.totalAmount || 0
                      )}
                </p>
                <p className="text-xs text-muted-foreground mt-1">
                  {showOrderCounts
                    ? `${formatCurrency(
                        sortedData.moneyReport?.summary?.totalAmount || 0
                      )} total amount`
                    : `${(
                        sortedData.moneyReport?.summary?.totalOrders || 0
                      ).toLocaleString()} orders`}
                </p>
              </div>
              <div className="bg-primary/10 p-3 rounded-full">
                {showOrderCounts ? (
                  <ShoppingCart className="text-primary h-6 w-6" />
                ) : (
                  <DollarSign className="text-primary h-6 w-6" />
                )}
              </div>
            </div>

            <div className="flex items-center justify-between p-4 bg-muted rounded-lg">
              <div>
                <p className="text-sm font-medium text-muted-foreground">
                  {showOrderCounts ? 'Orders with Tip' : 'Total Tips'}
                </p>
                <p
                  className="text-2xl font-bold text-foreground"
                  data-testid={
                    showOrderCounts ? 'text-orders-with-tip' : 'text-total-tips'
                  }
                >
                  {showOrderCounts
                    ? (
                        tipSummary?.totalOrdersWithTip ||
                        sortedData.moneyReport?.summary?.totalOrdersWithTip ||
                        0
                      ).toLocaleString()
                    : formatCurrency(tipSummary?.totalTip || 0)}
                </p>
                <p className="text-xs text-muted-foreground mt-1">
                  {showOrderCounts
                    ? `${formatCurrency(tipSummary?.totalTip || 0)} tip amount`
                    : `${(
                        tipSummary?.totalOrdersWithTip ||
                        sortedData.moneyReport?.summary?.totalOrdersWithTip ||
                        0
                      ).toLocaleString()} orders`}
                </p>
              </div>
              <div className="bg-green-100 p-3 rounded-full">
                <TrendingUp className="text-green-600 h-5 w-5" />
              </div>
            </div>

            <div className="flex items-center justify-between p-4 bg-muted rounded-lg">
              <div>
                <p className="text-sm font-medium text-muted-foreground">
                  {showOrderCounts ? 'Orders with Tax' : 'Total Tax Collected'}
                </p>
                <p
                  className="text-2xl font-bold text-foreground"
                  data-testid={
                    showOrderCounts ? 'text-orders-with-tax' : 'text-total-tax'
                  }
                >
                  {showOrderCounts
                    ? (
                        sortedData.moneyReport?.summary?.totalOrders || 0
                      ).toLocaleString()
                    : formatCurrency(
                        sortedData.moneyReport?.summary?.totalTax || 0
                      )}
                </p>
                <p className="text-xs text-muted-foreground mt-1">
                  {showOrderCounts
                    ? `${formatCurrency(
                        sortedData.moneyReport?.summary?.totalTax || 0
                      )} tax amount`
                    : `${(
                        sortedData.moneyReport?.summary?.totalOrders || 0
                      ).toLocaleString()} orders`}
                </p>
              </div>
              <div className="bg-orange-100 p-3 rounded-full">
                <Clock className="text-orange-600 h-5 w-5" />
              </div>
            </div>
          </div>

          {/* Promotions Summary Row */}
          {(sortedData.rewardReport ||
            sortedData.couponsDiscountsRewards?.summary?.rewards ||
            sortedData.couponsDiscountsRewards?.summary?.coupons ||
            sortedData.couponsDiscountsRewards?.summary?.discounts) && (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 mt-6 pt-6 border-t border-border">
              {/* Loyalty & Rewards Summary */}
              {(sortedData.rewardReport ||
                sortedData.couponsDiscountsRewards?.summary?.rewards) && (
                <div className="flex items-center justify-between p-4 bg-green-50 dark:bg-green-900/20 rounded-lg border border-green-200 dark:border-green-800">
                  <div>
                    <p className="text-sm font-medium text-green-700 dark:text-green-300">
                      Rewards Value Used
                    </p>
                    <p className="text-2xl font-bold text-green-800 dark:text-green-200">
                      {formatCurrency(
                        sortedData.rewardReport
                          ? sortedData.rewardReport.summary.totalUsedRewards ||
                              0
                          : sortedData.couponsDiscountsRewards?.summary?.rewards
                              ?.totalUsedRewards || 0
                      )}
                    </p>
                    <p className="text-xs text-green-600 dark:text-green-400 mt-1">
                      {(sortedData.rewardReport
                        ? sortedData.rewardReport.summary
                            .totalUsedRewardsPoints || 0
                        : sortedData.couponsDiscountsRewards?.summary?.rewards
                            ?.totalUsedRewardPoints || 0
                      ).toLocaleString()}{' '}
                      points used
                    </p>
                  </div>
                  <div className="bg-green-100 dark:bg-green-800 p-3 rounded-full">
                    <Gift className="text-green-600 dark:text-green-300 h-5 w-5" />
                  </div>
                </div>
              )}

              {/* Coupons Summary */}
              {sortedData.couponsDiscountsRewards?.summary?.coupons && (
                <div className="flex items-center justify-between p-4 bg-purple-50 dark:bg-purple-900/20 rounded-lg border border-purple-200 dark:border-purple-800">
                  <div>
                    <p className="text-sm font-medium text-purple-700 dark:text-purple-300">
                      Coupon Value
                    </p>
                    <p className="text-2xl font-bold text-purple-800 dark:text-purple-200">
                      {formatCurrency(
                        sortedData.couponsDiscountsRewards.summary.coupons
                          .totalAmount || 0
                      )}
                    </p>
                    <p className="text-xs text-purple-600 dark:text-purple-400 mt-1">
                      {(
                        sortedData.couponsDiscountsRewards.summary.coupons
                          .totalCount || 0
                      ).toLocaleString()}{' '}
                      uses
                    </p>
                  </div>
                  <div className="bg-purple-100 dark:bg-purple-800 p-3 rounded-full">
                    <Percent className="text-purple-600 dark:text-purple-300 h-5 w-5" />
                  </div>
                </div>
              )}

              {/* Discounts Summary */}
              {sortedData.couponsDiscountsRewards?.summary?.discounts && (
                <div className="flex items-center justify-between p-4 bg-blue-50 dark:bg-blue-900/20 rounded-lg border border-blue-200 dark:border-blue-800">
                  <div>
                    <p className="text-sm font-medium text-blue-700 dark:text-blue-300">
                      Discount Value (includes coupons)
                    </p>
                    <p className="text-2xl font-bold text-blue-800 dark:text-blue-200">
                      {formatCurrency(
                        Math.abs(
                          sortedData.couponsDiscountsRewards.summary.discounts
                            .totalAmount || 0
                        )
                      )}
                    </p>
                    <p className="text-xs text-blue-600 dark:text-blue-400 mt-1">
                      {(
                        sortedData.couponsDiscountsRewards.summary.discounts
                          .totalCount || 0
                      ).toLocaleString()}{' '}
                      applications
                    </p>
                  </div>
                  <div className="bg-blue-100 dark:bg-blue-800 p-3 rounded-full">
                    <TrendingDown className="text-blue-600 dark:text-blue-300 h-5 w-5" />
                  </div>
                </div>
              )}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Payment Methods & Sales by Source */}
      <Card className="print-card">
        <CardHeader>
          <CardTitle className="flex items-center">
            <PieChart className="text-primary mr-3 h-5 w-5" />
            Revenue Breakdown
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            {/* Tender Types */}
            <div>
              <h4 className="text-md font-medium text-foreground mb-3">
                Payment Methods
              </h4>
              <div className="space-y-3">
                {sortedData.moneyReport?.tenderTypes?.map((tender, index) => {
                  // TODO: fix this
                  const isCard = tender._id.toLowerCase() === 'card';
                  const hasCardBreakdown =
                    isCard &&
                    sortedData.moneyReport?.cardTypeBreakdown &&
                    sortedData.moneyReport.cardTypeBreakdown.length > 0;

                  if (isCard && hasCardBreakdown) {
                    // Special layout for Card with breakdown
                    return (
                      <div key={index} className="bg-muted rounded-lg">
                        <Accordion type="single" collapsible>
                          <AccordionItem
                            value="card-details"
                            className="border-none"
                          >
                            <AccordionTrigger className="hover:no-underline p-3 text-base">
                              <div className="flex items-center justify-between w-full pr-2">
                                <div className="flex items-center">
                                  {getTenderIcon(tender._id)}
                                  <span className="font-medium text-foreground ml-3 capitalize">
                                    {tender._id}
                                  </span>
                                </div>
                                <div className="text-right">
                                  <div className="font-semibold text-foreground">
                                    {formatCurrency(tender.totalAmount)}
                                  </div>
                                  <div className="text-xs text-muted-foreground">
                                    {tender.count} transactions
                                  </div>
                                </div>
                              </div>
                            </AccordionTrigger>
                            <AccordionContent className="px-3 pb-3 pt-0">
                              {/* Card Type Grid */}
                              <div className="grid grid-cols-1 gap-3">
                                {sortedData.moneyReport?.cardTypeBreakdown?.map(
                                  (cardType, cardIndex) => (
                                    <div
                                      key={cardIndex}
                                      className="flex items-center justify-between p-3 bg-background rounded-md border"
                                    >
                                      <div className="flex items-center">
                                        <span className="font-medium text-foreground">
                                          {getCardTypeDisplayName(
                                            cardType.cardType
                                          )}
                                        </span>
                                      </div>
                                      <div className="text-right">
                                        <div className="font-semibold text-foreground">
                                          {formatCurrency(cardType.totalAmount)}
                                        </div>
                                        <div className="text-xs text-muted-foreground">
                                          {cardType.count} transactions
                                        </div>
                                      </div>
                                    </div>
                                  )
                                )}
                              </div>
                            </AccordionContent>
                          </AccordionItem>
                        </Accordion>
                      </div>
                    );
                  } else {
                    // Regular layout for non-card payment methods
                    return (
                      <div
                        key={index}
                        className="flex items-center justify-between p-3 bg-muted rounded-lg"
                      >
                        <div className="flex items-center">
                          {getTenderIcon(tender._id)}
                          <span className="font-medium text-foreground ml-3 capitalize">
                            {tender._id}
                          </span>
                        </div>
                        <div className="text-right">
                          <div className="font-semibold text-foreground">
                            {formatCurrency(tender.totalAmount)}
                          </div>
                          <div className="text-xs text-muted-foreground">
                            {tender.count} transactions
                          </div>
                        </div>
                      </div>
                    );
                  }
                })}
              </div>
            </div>

            {/* Sales by Source */}
            <div>
              <h4 className="text-md font-medium text-foreground mb-3">
                Sales by Channel
              </h4>
              <div className="space-y-3">
                {sortedData.moneyReport?.salesBySource?.map((source, index) => (
                  <div
                    key={index}
                    className="flex items-center justify-between p-3 bg-muted rounded-lg"
                  >
                    <div className="flex items-center">
                      {getSourceIcon(source._id)}
                      <span className="font-medium text-foreground ml-3">
                        {getProviderDisplayName(source._id)}
                      </span>
                    </div>
                    <div className="text-right">
                      <div className="font-semibold text-foreground">
                        {formatCurrency(source.total)}
                      </div>
                      <div className="text-xs text-muted-foreground">
                        {source.count} orders
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Customer Analytics */}
      <Card className="print-card">
        <CardHeader>
          <CardTitle className="flex items-center">
            <Users className="text-primary mr-3 h-5 w-5" />
            Customer Analytics
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="bg-muted p-4 rounded-lg">
              <h4 className="font-medium text-foreground mb-2">
                Guest Customers
              </h4>
              <div className="text-2xl font-bold text-foreground">
                {(
                  customerData?.guestCustomers?.count ||
                  customerData?.breakdownBySegment?.find(
                    (s) => s.segment === 'guest'
                  )?.customerCount ||
                  0
                ).toLocaleString()}
              </div>
              <div className="text-sm text-muted-foreground">
                {customerData?.guestCustomers?.orderCount ||
                  customerData?.breakdownBySegment?.find(
                    (s) => s.segment === 'guest'
                  )?.orderCount ||
                  0}{' '}
                orders
              </div>
            </div>

            <div className="bg-muted p-4 rounded-lg">
              <h4 className="font-medium text-foreground mb-2">
                New Customers
              </h4>
              <div className="text-2xl font-bold text-foreground">
                {(
                  customerData?.newCustomers?.count ||
                  customerData?.breakdownBySegment?.find(
                    (s) => s.segment === 'new'
                  )?.customerCount ||
                  0
                ).toLocaleString()}
              </div>
              <div className="text-sm text-muted-foreground">
                {customerData?.newCustomers?.orderCount ||
                  customerData?.breakdownBySegment?.find(
                    (s) => s.segment === 'new'
                  )?.orderCount ||
                  0}{' '}
                orders
              </div>
            </div>

            <div className="bg-muted p-4 rounded-lg">
              <h4 className="font-medium text-foreground mb-2">
                Returning Customers
              </h4>
              <div className="text-2xl font-bold text-foreground">
                {(
                  customerData?.returningCustomers?.count ||
                  customerData?.breakdownBySegment?.find(
                    (s) => s.segment === 'returning'
                  )?.customerCount ||
                  0
                ).toLocaleString()}
              </div>
              <div className="text-sm text-muted-foreground">
                {customerData?.returningCustomers?.orderCount ||
                  customerData?.breakdownBySegment?.find(
                    (s) => s.segment === 'returning'
                  )?.orderCount ||
                  0}{' '}
                orders
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* TEMPORARILY HIDDEN - Delivery Performance */}

      {/* Tips Analysis */}
      {(data.tipReport || data.tipSummary) && (
        <Card className="print-card">
          <CardHeader>
            <CardTitle className="flex items-center">
              <Gift className="text-primary mr-3 h-5 w-5" />
              Tips Analysis
            </CardTitle>
          </CardHeader>
          <CardContent>
            {/* Tips Overview - Main Summary */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
              <div className="text-center">
                <div className="text-2xl font-bold text-foreground">
                  {formatCurrency(tipSummary?.totalTip || 0)}
                </div>
                <div className="text-sm text-muted-foreground">Total Tips</div>
              </div>

              <div className="text-center">
                <div className="text-2xl font-bold text-foreground">
                  {formatCurrency(tipSummary?.averageTipPerOrder || 0)}
                </div>
                <div className="text-sm text-muted-foreground">
                  Avg Tip per Order
                </div>
              </div>

              <div className="text-center">
                <div className="text-2xl font-bold text-foreground">
                  {typeof tipSummary?.tipParticipationRate === 'number'
                    ? tipSummary?.tipParticipationRate.toFixed(1)
                    : tipSummary?.tipParticipationRate || 0}
                  %
                </div>
                <div className="text-sm text-muted-foreground">
                  Tip Participation Rate
                </div>
              </div>

              <div className="text-center">
                <div className="text-2xl font-bold text-foreground">
                  {formatCurrency(tipSummary?.averageTipPerOrderWithTips || 0)}
                </div>
                <div className="text-sm text-muted-foreground">
                  Avg Tip (Orders with Tips)
                </div>
              </div>
            </div>

            {/* Tips Breakdown - Accordion Format */}
            <Accordion
              type="multiple"
              defaultValue={[
                'tips-by-type',
                'tips-by-payment',
                'tips-by-source',
              ]}
              className="space-y-3"
            >
              {/* Tips by Type Accordion */}
              <AccordionItem value="tips-by-type" className="border rounded-lg">
                <AccordionTrigger className="px-4 hover:no-underline">
                  <div className="flex items-center justify-between w-full pr-4">
                    <div className="flex items-center">
                      <Gift className="h-4 w-4 mr-2 text-primary" />
                      <span className="font-semibold">Tips by Type</span>
                    </div>
                    <div className="text-right">
                      <div className="font-bold">
                        {formatCurrency(tipSummary?.totalTip || 0)}
                      </div>
                      <div className="text-xs text-muted-foreground">
                        {tipSummary?.totalOrdersWithTip || 0} orders
                      </div>
                    </div>
                  </div>
                </AccordionTrigger>
                <AccordionContent className="px-3 pb-3 pt-0">
                  <div className="grid grid-cols-1 gap-3">
                    {/* Kitchen Tips */}
                    <div className="flex items-center justify-between p-3 bg-background rounded-md border">
                      <div className="flex items-center">
                        <span className="font-medium text-foreground">
                          Kitchen Tips
                        </span>
                      </div>
                      <div className="text-right">
                        <div className="font-semibold text-foreground">
                          {formatCurrency(tipSummary?.totalKitchenTip || 0)}
                        </div>
                        <div className="text-xs text-muted-foreground">
                          {typeof tipSummary?.tipDistribution
                            ?.kitchenTipPercentage === 'number'
                            ? tipSummary?.tipDistribution.kitchenTipPercentage.toFixed(
                                1
                              )
                            : tipSummary?.tipDistribution
                                ?.kitchenTipPercentage || 0}
                          % of total
                        </div>
                      </div>
                    </div>

                    {/* Driver Tips */}
                    <div className="flex items-center justify-between p-3 bg-background rounded-md border">
                      <div className="flex items-center">
                        <span className="font-medium text-foreground">
                          Driver Tips
                        </span>
                      </div>
                      <div className="text-right">
                        <div className="font-semibold text-foreground">
                          {formatCurrency(tipSummary?.totalDriverTip || 0)}
                        </div>
                        <div className="text-xs text-muted-foreground">
                          {typeof tipSummary?.tipDistribution
                            ?.driverTipPercentage === 'number'
                            ? tipSummary?.tipDistribution.driverTipPercentage.toFixed(
                                1
                              )
                            : tipSummary?.tipDistribution
                                ?.driverTipPercentage || 0}
                          % of total
                        </div>
                      </div>
                    </div>
                  </div>
                </AccordionContent>
              </AccordionItem>

              {/* Tips by Payment Method Accordion */}
              {getTipByPaymentType() && getTipByPaymentType()?.length > 0 && (
                <AccordionItem
                  value="tips-by-payment"
                  className="border rounded-lg"
                >
                  <AccordionTrigger className="px-4 hover:no-underline">
                    <div className="flex items-center justify-between w-full pr-4">
                      <div className="flex items-center">
                        <CreditCard className="h-4 w-4 mr-2 text-primary" />
                        <span className="font-semibold">
                          Tips by Payment Method
                        </span>
                      </div>
                      <div className="text-right">
                        <div className="font-bold">
                          {formatCurrency(tipSummary?.totalTip || 0)}
                        </div>
                        <div className="text-xs text-muted-foreground">
                          {getTipByPaymentType()?.length} methods
                        </div>
                      </div>
                    </div>
                  </AccordionTrigger>
                  <AccordionContent className="px-3 pb-3 pt-0">
                    <div className="grid grid-cols-1 gap-3">
                      {getTipByPaymentType()?.map((tipMethod, index) => (
                        <div
                          key={index}
                          className="flex items-center justify-between p-3 bg-background rounded-md border"
                        >
                          <div className="flex items-center">
                            {getTenderIcon(tipMethod.paymentType || 'other')}
                            <span className="font-medium text-foreground ml-2 capitalize">
                              {tipMethod.paymentType === 'other'
                                ? 'Other'
                                : tipMethod.paymentType}
                            </span>
                          </div>
                          <div className="text-right">
                            <div className="font-semibold text-foreground">
                              {formatCurrency(tipMethod.tipAmount || 0)}
                            </div>
                            <div className="text-xs text-muted-foreground">
                              {tipMethod.orderCount || 0} orders
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </AccordionContent>
                </AccordionItem>
              )}

              {/* Tips by Source Accordion */}
              {getTipBySource() && getTipBySource()?.length > 0 && (
                <AccordionItem
                  value="tips-by-source"
                  className="border rounded-lg"
                >
                  <AccordionTrigger className="px-4 hover:no-underline">
                    <div className="flex items-center justify-between w-full pr-4">
                      <div className="flex items-center">
                        <Store className="h-4 w-4 mr-2 text-primary" />
                        <span className="font-semibold">Tips by Source</span>
                      </div>
                      <div className="text-right">
                        <div className="font-bold">
                          {formatCurrency(tipSummary?.totalTip || 0)}
                        </div>
                        <div className="text-xs text-muted-foreground">
                          {getTipBySource()?.length} sources
                        </div>
                      </div>
                    </div>
                  </AccordionTrigger>
                  <AccordionContent className="px-3 pb-3 pt-0">
                    <div className="grid grid-cols-1 gap-3">
                      {getTipBySource()?.map((tipSource, index) => (
                        <div
                          key={index}
                          className="p-3 bg-background rounded-md border"
                        >
                          <div className="flex justify-between items-center mb-2">
                            <span className="font-medium">
                              {getProviderDisplayName(tipSource.source)}
                            </span>
                            <div className="text-right">
                              <div className="font-semibold">
                                {formatCurrency(tipSource.tipAmount)}
                              </div>
                              <div className="text-xs text-muted-foreground">
                                {tipSource.orderCount} orders
                              </div>
                            </div>
                          </div>
                          {/* Tip Types Breakdown */}
                          <div className="border-t border-border pt-3 mt-3">
                            <div className="grid grid-cols-2 gap-3">
                              {/* Kitchen Tip */}
                              <div className="flex flex-col p-2 bg-muted rounded-md">
                                <span className="text-xs text-muted-foreground">
                                  Kitchen Tip
                                </span>
                                <span className="font-semibold text-foreground">
                                  {formatCurrency(tipSource.kitchenTipAmount)}
                                </span>
                              </div>

                              {/* Driver Tip */}
                              <div className="flex flex-col p-2 bg-muted rounded-md">
                                <span className="text-xs text-muted-foreground">
                                  Driver Tip
                                </span>
                                <span className="font-semibold text-foreground">
                                  {formatCurrency(tipSource.driverTipAmount)}
                                </span>
                              </div>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </AccordionContent>
                </AccordionItem>
              )}
            </Accordion>
          </CardContent>
        </Card>
      )}

      {/* Delivery Performance */}
      {data.deliveryReport && (
        <Card className="print-card">
          <CardHeader>
            <CardTitle className="flex items-center">
              <Truck className="text-primary mr-3 h-5 w-5" />
              Delivery Performance
            </CardTitle>
          </CardHeader>
          <CardContent>
            {(() => {
              // Get dispatch data
              const dispatchCosts = data.deliveryReport.deliveryCosts?.find(
                (item) => item.deliveryProvider === 'dispatch'
              );
              const dispatchPrices = data.deliveryReport.deliveryPrices?.find(
                (item) => item.deliveryProvider === 'dispatch'
              );
              const dispatchTips = data.deliveryReport.driverTips?.find(
                (item) => item.deliveryProvider === 'dispatch'
              );

              // Get in-house data
              const selfCosts = data.deliveryReport.deliveryCosts?.find(
                (item) => item.deliveryProvider === 'self'
              );
              const selfPrices = data.deliveryReport.deliveryPrices?.find(
                (item) => item.deliveryProvider === 'self'
              );
              const selfTips = data.deliveryReport.driverTips?.find(
                (item) => item.deliveryProvider === 'self'
              );
              const selfDriverBreakdown = data.deliveryReport.driverBreakdown;

              // Calculate totals
              const totalDispatchFees =
                (dispatchCosts?.totalDeliveryQuotes || 0) / 100;
              const totalDispatchTips = dispatchTips?.totalDriverTips || 0;
              const totalDispatchCosts = totalDispatchFees + totalDispatchTips;

              const totalSelfFees = (selfCosts?.totalDeliveryQuotes || 0) / 100;
              const totalSelfTips = selfTips?.totalDriverTips || 0;
              const totalSelfCosts = totalSelfFees + totalSelfTips;

              return (
                <>
                  {/* Delivery Performance Summary Table */}
                  <div className="overflow-x-auto mb-8">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Provider</TableHead>
                          <TableHead className="text-right">
                            Deliveries
                          </TableHead>
                          <TableHead className="text-right">
                            <div>Delivery Fees</div>
                            <div className="text-xs font-normal text-muted-foreground">
                              by providers
                            </div>
                          </TableHead>
                          <TableHead className="text-right">
                            <div>Driver Tips</div>
                            <div className="text-xs font-normal text-muted-foreground">
                              by providers
                            </div>
                          </TableHead>
                          <TableHead className="text-right">
                            <div>Total Costs</div>
                            <div className="text-xs font-normal text-muted-foreground">
                              by providers
                            </div>
                          </TableHead>
                          <TableHead className="text-right">
                            <div>Collected Fees</div>
                            <div className="text-xs font-normal text-muted-foreground">
                              from customers
                            </div>
                          </TableHead>
                          <TableHead className="text-right">
                            <div>Avg Fee/Order</div>
                            <div className="text-xs font-normal text-muted-foreground">
                              from customers
                            </div>
                          </TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {/* AI Dispatch Row */}
                        {dispatchCosts && (
                          <TableRow>
                            <TableCell className="font-medium">
                              <div className="flex items-center">
                                <Smartphone className="h-4 w-4 mr-2" />
                                AI Dispatch
                              </div>
                            </TableCell>
                            <TableCell className="text-right">
                              {(
                                dispatchCosts?.orderCount || 0
                              ).toLocaleString()}
                            </TableCell>
                            <TableCell className="text-right">
                              {formatCurrency(totalDispatchFees)}
                            </TableCell>
                            <TableCell className="text-right">
                              {formatCurrency(totalDispatchTips)}
                            </TableCell>
                            <TableCell className="text-right">
                              {formatCurrency(totalDispatchCosts)}
                            </TableCell>
                            <TableCell className="text-right">
                              {formatCurrency(
                                dispatchPrices?.totalCustomerDeliveryFees || 0
                              )}
                            </TableCell>
                            <TableCell className="text-right">
                              {formatCurrency(
                                dispatchCosts?.averageDeliveryFee || 0
                              )}
                            </TableCell>
                          </TableRow>
                        )}

                        {/* In-House Row */}
                        {selfCosts && (
                          <TableRow>
                            <TableCell className="font-medium">
                              <div className="flex items-center">
                                <Truck className="h-4 w-4 mr-2" />
                                In-House Delivery
                              </div>
                            </TableCell>
                            <TableCell className="text-right">
                              {(selfCosts?.orderCount || 0).toLocaleString()}
                            </TableCell>
                            <TableCell className="text-right">
                              {formatCurrency(totalSelfFees)}
                            </TableCell>
                            <TableCell className="text-right">
                              {formatCurrency(totalSelfTips)}
                            </TableCell>
                            <TableCell className="text-right">
                              {formatCurrency(totalSelfCosts)}
                            </TableCell>
                            <TableCell className="text-right">
                              {formatCurrency(
                                selfPrices?.totalCustomerDeliveryFees || 0
                              )}
                            </TableCell>
                            <TableCell className="text-right">
                              {formatCurrency(
                                selfCosts?.averageDeliveryFee || 0
                              )}
                            </TableCell>
                          </TableRow>
                        )}
                      </TableBody>
                    </Table>
                  </div>

                  {/* Driver Performance Table */}
                  {selfDriverBreakdown?.length > 0 && (
                    <div className="mt-8 pt-8 border-t border-border">
                      <h4 className="text-lg font-semibold text-foreground mb-4 flex items-center">
                        <Truck className="h-5 w-5 mr-2" />
                        Driver Performance
                      </h4>
                      <div className="overflow-x-auto">
                        <Table>
                          <TableHeader>
                            <TableRow>
                              <TableHead>Driver</TableHead>
                              <TableHead className="text-right">
                                Orders
                              </TableHead>
                              <TableHead className="text-right">
                                Total Tips
                              </TableHead>
                              <TableHead className="text-right">
                                Avg Tip
                              </TableHead>
                              <TableHead className="text-right">
                                Total Fees
                              </TableHead>
                              <TableHead className="text-right">
                                Distance
                              </TableHead>
                              <TableHead className="text-right">
                                Avg Duration
                              </TableHead>
                            </TableRow>
                          </TableHeader>
                          <TableBody>
                            {selfDriverBreakdown.map((driver, index) => (
                              <TableRow key={index}>
                                <TableCell className="font-medium">
                                  <div>{driver.driverName}</div>
                                  <div className="text-xs text-muted-foreground">
                                    {driver.driverPhone}
                                  </div>
                                </TableCell>
                                <TableCell className="text-right">
                                  {driver.orderCount}
                                </TableCell>
                                <TableCell className="text-right">
                                  {formatCurrency(driver.totalDriverTips)}
                                </TableCell>
                                <TableCell className="text-right">
                                  {formatCurrency(driver.averageDriverTip)}
                                </TableCell>
                                <TableCell className="text-right">
                                  {formatCurrency(driver.totalDeliveryFees)}
                                </TableCell>
                                <TableCell className="text-right">
                                  {driver.totalDistance.toFixed(2)} mi
                                </TableCell>
                                <TableCell className="text-right">
                                  {driver.averageDuration} min
                                </TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </div>
                    </div>
                  )}
                </>
              );
            })()}
          </CardContent>
        </Card>
      )}

      {/* Third-Party Platform Performance */}
      {data.deliveryReport?.thirdPartyBreakdown?.length > 0 && (
        <Card className="print-card">
          <CardHeader>
            <CardTitle className="flex items-center">
              <ListChecks className="text-primary mr-3 h-5 w-5" />
              Platform Performance
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Platform</TableHead>
                    <TableHead>Delivery Provider</TableHead>
                    <TableHead>Orders</TableHead>
                    <TableHead className="text-right">
                      Avg Delivery Fee
                    </TableHead>
                    <TableHead className="text-right">Total Tips</TableHead>
                    <TableHead className="text-right">Avg Tip</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {data.deliveryReport?.thirdPartyBreakdown?.flatMap(
                    (deliveryProviderGroup) =>
                      deliveryProviderGroup.providerBreakdown?.map(
                        (platform, index) => (
                          <TableRow
                            key={`${deliveryProviderGroup.deliveryProvider}-${platform.provider}-${index}`}
                          >
                            <TableCell className="font-medium">
                              {getProviderDisplayName(platform.provider)}
                            </TableCell>
                            <TableCell className="capitalize">
                              {getDeliveryProviderDisplayName(
                                deliveryProviderGroup.deliveryProvider
                              )}
                            </TableCell>
                            <TableCell>
                              {platform.orderCount.toLocaleString()}
                            </TableCell>
                            <TableCell className="text-right">
                              {formatCurrency(platform.averageDeliveryFee)}
                            </TableCell>
                            <TableCell className="text-right">
                              {formatCurrency(platform.totalTips)}
                            </TableCell>
                            <TableCell className="text-right">
                              {formatCurrency(platform.averageTip)}
                            </TableCell>
                          </TableRow>
                        )
                      ) || []
                  )}
                </TableBody>
              </Table>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Loyalty and Rewards */}
      {(sortedData.rewardReport ||
        sortedData.couponsDiscountsRewards?.summary?.rewards) && (
        <Card className="print-card">
          <CardHeader>
            <CardTitle className="flex items-center">
              <Gift className="text-primary mr-3 h-5 w-5" />
              Loyalty and Rewards
            </CardTitle>
          </CardHeader>
          <CardContent>
            {/* Rewards Summary Overview */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-6">
              <div className="text-center">
                <div className="text-2xl font-bold text-green-600">
                  {sortedData.rewardReport
                    ? formatCurrency(
                        sortedData.rewardReport.summary.totalEarnedRewards || 0
                      )
                    : formatCurrency(
                        sortedData.couponsDiscountsRewards?.rewards
                          ?.totalEarnedRewards || 0
                      )}
                </div>
                <div className="text-sm text-muted-foreground">
                  Value Earned
                </div>
              </div>

              <div className="text-center">
                <div className="text-2xl font-bold text-green-600">
                  {sortedData.rewardReport
                    ? formatCurrency(
                        sortedData.rewardReport.summary.totalUsedRewards || 0
                      )
                    : formatCurrency(
                        sortedData.couponsDiscountsRewards?.summary?.rewards
                          ?.totalUsedRewards || 0
                      )}
                </div>
                <div className="text-sm text-muted-foreground">
                  Value Redeemed
                </div>
              </div>

              <div className="text-center">
                <div className="text-2xl font-bold text-green-600">
                  {sortedData.rewardReport
                    ? (
                        sortedData.rewardReport.summary
                          .totalEarnedRewardsPoints || 0
                      ).toLocaleString()
                    : (
                        sortedData.couponsDiscountsRewards?.rewards
                          ?.totalEarnedRewardPoints || 0
                      ).toLocaleString()}
                </div>
                <div className="text-sm text-muted-foreground">
                  Points Earned
                </div>
              </div>

              <div className="text-center">
                <div className="text-2xl font-bold text-green-600">
                  {sortedData.rewardReport
                    ? (
                        sortedData.rewardReport.summary
                          .totalUsedRewardsPoints || 0
                      ).toLocaleString()
                    : (
                        sortedData.couponsDiscountsRewards?.summary?.rewards
                          ?.totalUsedRewardPoints || 0
                      ).toLocaleString()}
                </div>
                <div className="text-sm text-muted-foreground">Points Used</div>
              </div>
            </div>

            {/* Rewards Details */}
            <div className="space-y-6">
              {sortedData.rewardReport ? (
                <div className="p-4 bg-green-50 dark:bg-green-900/20 rounded-lg border border-green-200 dark:border-green-800">
                  <h5 className="font-medium text-green-800 dark:text-green-200 mb-3">
                    Rewards Program Details
                  </h5>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <h6 className="text-sm font-medium text-green-700 dark:text-green-300">
                        Rewards Earned
                      </h6>
                      <div className="space-y-1 text-sm">
                        <div className="flex justify-between">
                          <span>Total Value</span>
                          <span className="font-semibold">
                            {formatCurrency(
                              sortedData.rewardReport.summary
                                .totalEarnedRewards || 0
                            )}
                          </span>
                        </div>
                        <div className="flex justify-between">
                          <span>Total Points</span>
                          <span className="font-semibold">
                            {(
                              sortedData.rewardReport.summary
                                .totalEarnedRewardsPoints || 0
                            ).toLocaleString()}
                          </span>
                        </div>
                        <div className="flex justify-between">
                          <span>Orders with Rewards</span>
                          <span className="font-semibold">
                            {(
                              sortedData.rewardReport.summary
                                .ordersWithRewardsEarned || 0
                            ).toLocaleString()}
                          </span>
                        </div>
                      </div>
                    </div>
                    <div className="space-y-2">
                      <h6 className="text-sm font-medium text-green-700 dark:text-green-300">
                        Rewards Used
                      </h6>
                      <div className="space-y-1 text-sm">
                        <div className="flex justify-between">
                          <span>Total Value</span>
                          <span className="font-semibold">
                            {formatCurrency(
                              sortedData.rewardReport.summary
                                .totalUsedRewards || 0
                            )}
                          </span>
                        </div>
                        <div className="flex justify-between">
                          <span>Total Points</span>
                          <span className="font-semibold">
                            {(
                              sortedData.rewardReport.summary
                                .totalUsedRewardsPoints || 0
                            ).toLocaleString()}
                          </span>
                        </div>
                        <div className="flex justify-between">
                          <span>Orders with Redemptions</span>
                          <span className="font-semibold">
                            {(
                              sortedData.rewardReport.summary
                                .ordersWithRewardsUsed || 0
                            ).toLocaleString()}
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              ) : (
                /* Legacy Rewards Details */
                <div className="p-4 bg-green-50 dark:bg-green-900/20 rounded-lg border border-green-200 dark:border-green-800">
                  <h5 className="font-medium text-green-800 dark:text-green-200 mb-3">
                    Rewards Program Details
                  </h5>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="space-y-1 text-sm">
                      <div className="flex justify-between">
                        <span>Points Earned</span>
                        <span className="font-semibold">
                          {(
                            sortedData.couponsDiscountsRewards?.rewards
                              ?.totalEarnedRewardPoints || 0
                          ).toLocaleString()}
                        </span>
                      </div>
                      <div className="flex justify-between">
                        <span>Value Earned</span>
                        <span className="font-semibold">
                          {formatCurrency(
                            sortedData.couponsDiscountsRewards?.rewards
                              ?.totalEarnedRewards || 0
                          )}
                        </span>
                      </div>
                    </div>
                    <div className="space-y-1 text-sm">
                      <div className="flex justify-between">
                        <span>Points Used</span>
                        <span className="font-semibold">
                          {(
                            sortedData.couponsDiscountsRewards?.summary?.rewards
                              ?.totalUsedRewardPoints || 0
                          ).toLocaleString()}
                        </span>
                      </div>
                      <div className="flex justify-between">
                        <span>Value Redeemed</span>
                        <span className="font-semibold">
                          {formatCurrency(
                            sortedData.couponsDiscountsRewards?.summary?.rewards
                              ?.totalUsedRewards || 0
                          )}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Coupons */}
      {sortedData.couponsDiscountsRewards?.coupons?.length > 0 && (
        <Card className="print-card">
          <CardHeader>
            <CardTitle className="flex items-center">
              <Gift className="text-primary mr-3 h-5 w-5" />
              Coupons
            </CardTitle>
          </CardHeader>
          <CardContent>
            {/* Coupons Summary Overview */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
              <div className="text-center">
                <div className="text-2xl font-bold text-purple-600">
                  {formatCurrency(
                    sortedData.couponsDiscountsRewards?.summary?.coupons
                      ?.totalAmount || 0
                  )}
                </div>
                <div className="text-sm text-muted-foreground">Total Value</div>
              </div>

              <div className="text-center">
                <div className="text-2xl font-bold text-purple-600">
                  {sortedData.couponsDiscountsRewards?.summary?.coupons
                    ?.totalCount || 0}
                </div>
                <div className="text-sm text-muted-foreground">Total Uses</div>
              </div>

              <div className="text-center">
                <div className="text-2xl font-bold text-purple-600">
                  {formatCurrency(
                    (sortedData.couponsDiscountsRewards?.summary?.coupons
                      ?.totalAmount || 0) /
                      Math.max(
                        1,
                        sortedData.couponsDiscountsRewards?.summary?.coupons
                          ?.totalCount || 1
                      )
                  )}
                </div>
                <div className="text-sm text-muted-foreground">
                  Average Value
                </div>
              </div>
            </div>

            <div className="space-y-6">
              {/* Coupon Performance Details */}
              <div>
                <h4 className="font-medium text-foreground mb-4">
                  Coupon Performance Details
                </h4>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
                  {sortedData.couponsDiscountsRewards?.coupons?.map(
                    (coupon, index) => (
                      <div key={index} className="p-4 bg-muted rounded-lg">
                        <div className="space-y-2">
                          <div>
                            <div className="font-semibold text-foreground text-sm">
                              {coupon.couponCode}
                            </div>
                            <div className="text-xs text-muted-foreground">
                              {coupon.couponName}
                            </div>
                          </div>
                          <div className="space-y-1">
                            <div className="flex justify-between items-center">
                              <span className="text-xs text-muted-foreground">
                                Total
                              </span>
                              <span className="font-bold text-foreground">
                                {formatCurrency(coupon.totalCouponAmount)}
                              </span>
                            </div>
                            <div className="flex justify-between items-center">
                              <span className="text-xs text-muted-foreground">
                                Uses
                              </span>
                              <span className="text-sm font-medium">
                                {coupon.count}
                              </span>
                            </div>
                            <div className="flex justify-between items-center">
                              <span className="text-xs text-muted-foreground">
                                Avg/use
                              </span>
                              <span className="text-sm font-medium">
                                {formatCurrency(
                                  coupon.totalCouponAmount / coupon.count
                                )}
                              </span>
                            </div>
                          </div>
                        </div>
                      </div>
                    )
                  )}
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Discounts */}
      {sortedData.couponsDiscountsRewards?.discounts?.length > 0 && (
        <Card className="print-card">
          <CardHeader>
            <CardTitle className="flex items-center">
              <Gift className="text-primary mr-3 h-5 w-5" />
              Discounts (includes coupons)
            </CardTitle>
          </CardHeader>
          <CardContent>
            {/* Discounts Summary Overview */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
              <div className="text-center">
                <div className="text-2xl font-bold text-blue-600">
                  {formatCurrency(
                    Math.abs(
                      sortedData.couponsDiscountsRewards?.summary?.discounts
                        ?.totalAmount || 0
                    )
                  )}
                </div>
                <div className="text-sm text-muted-foreground">Total Value</div>
              </div>

              <div className="text-center">
                <div className="text-2xl font-bold text-blue-600">
                  {sortedData.couponsDiscountsRewards?.summary?.discounts
                    ?.totalCount || 0}
                </div>
                <div className="text-sm text-muted-foreground">
                  Total Applications
                </div>
              </div>

              <div className="text-center">
                <div className="text-2xl font-bold text-blue-600">
                  {formatCurrency(
                    Math.abs(
                      (sortedData.couponsDiscountsRewards?.summary?.discounts
                        ?.totalAmount || 0) /
                        Math.max(
                          1,
                          sortedData.couponsDiscountsRewards?.summary?.discounts
                            ?.totalCount || 1
                        )
                    )
                  )}
                </div>
                <div className="text-sm text-muted-foreground">
                  Average Value
                </div>
              </div>
            </div>

            <div className="space-y-6">
              {/* Discounts Details */}
              <div className="p-4 bg-blue-50 dark:bg-blue-900/20 rounded-lg border border-blue-200 dark:border-blue-800">
                <h5 className="font-medium text-blue-800 dark:text-blue-200 mb-3">
                  Discounts Applied
                </h5>
                <div className="space-y-2">
                  {sortedData.couponsDiscountsRewards?.discounts?.length > 0 ? (
                    sortedData.couponsDiscountsRewards?.discounts?.map(
                      (discount, index) => (
                        <div
                          key={index}
                          className="flex justify-between items-center"
                        >
                          <span className="text-blue-700 dark:text-blue-300">
                            {getDiscountTypeDisplayName(discount._id)}
                          </span>
                          <div className="text-right">
                            <div className="font-semibold text-blue-800 dark:text-blue-200">
                              {formatCurrency(
                                Math.abs(discount.totalDiscountAmount)
                              )}
                            </div>
                            <div className="text-xs text-blue-600 dark:text-blue-400">
                              {discount.count} applications
                            </div>
                          </div>
                        </div>
                      )
                    )
                  ) : (
                    <div className="flex justify-between items-center">
                      <span className="text-blue-700 dark:text-blue-300">
                        No discounts applied
                      </span>
                      <div className="text-right">
                        <div className="font-semibold text-blue-800 dark:text-blue-200">
                          $0.00
                        </div>
                        <div className="text-xs text-blue-600 dark:text-blue-400">
                          0 applications
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Detailed Source Breakdown */}
      <Card className="print-card">
        <CardHeader>
          <CardTitle className="flex items-center">
            <Store className="text-primary mr-3 h-5 w-5" />
            Detailed Channel Performance
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Channel</TableHead>
                  <TableHead>Orders</TableHead>
                  <TableHead>Order Type Breakdown</TableHead>
                  <TableHead className="text-right">Subtotal</TableHead>
                  <TableHead className="text-right">Tax</TableHead>
                  <TableHead className="text-right">Total</TableHead>
                  <TableHead className="text-right">AOV</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {console.log(sortedData)}
                {sortedData.orderBreakdownBySource?.breakdown?.map(
                  (source, index) => (
                    <TableRow key={index}>
                      <TableCell className="font-medium">
                        {getProviderDisplayName(source.provider)}
                      </TableCell>
                      <TableCell>
                        {source.orderCount.toLocaleString()}
                      </TableCell>
                      <TableCell>
                        <div className="text-xs">
                          {source.deliveryOrders > 0 && (
                            <div>Delivery: {source.deliveryOrders}</div>
                          )}
                          {source.pickupOrders > 0 && (
                            <div>Pickup: {source.pickupOrders}</div>
                          )}
                          {source.dineInOrders > 0 && (
                            <div>Dine-In: {source.dineInOrders}</div>
                          )}
                          {source.inStoreOrders > 0 && (
                            <div>In-Store: {source.inStoreOrders}</div>
                          )}
                        </div>
                      </TableCell>
                      <TableCell className="text-right">
                        {formatCurrency(source.subTotal)}
                      </TableCell>
                      <TableCell className="text-right">
                        {formatCurrency(source.tax)}
                      </TableCell>
                      <TableCell className="text-right">
                        {formatCurrency(source.total)}
                      </TableCell>
                      <TableCell className="text-right">
                        {formatCurrency(source.averageOrderValue)}
                      </TableCell>
                    </TableRow>
                  )
                )}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>

      {/* Daily Breakdown Table */}
      {(sortedData.dailyBreakdown || data.orderBreakdownByDate) && (
        <DailyBreakdownTable
          data={sortedData.dailyBreakdown}
          orderBreakdownByDate={data.orderBreakdownByDate}
          dateRange={data.dateRange}
          filters={data.filters}
        />
      )}

      {/* High-Performance Virtualized Order Table */}
      {data.detailedOrderReport && (
        <VirtualizedOrderTable
          data={{
            ...data.detailedOrderReport,
            dateRange: data.dateRange,
          }}
        />
      )}

      {/* Detailed Order Report */}
      {/* {data.detailedOrderReport && (
        <FinancialSummary data={data.detailedOrderReport} />
      )} */}

      {/* Report Footer */}
      <Card className="print-card">
        <CardContent className="pt-6">
          <div className="border-t border-border pt-4">
            <div className="flex flex-col md:flex-row md:items-center md:justify-between text-xs text-muted-foreground">
              <div>
                <p>
                  <strong>Report Generated By:</strong> POS Orders Analytics
                  Platform
                </p>
                <p>
                  <strong>Data Source:</strong> api.pos.orders.co
                </p>
              </div>
              <div className="mt-2 md:mt-0">
                <p>
                  <strong>Export Time:</strong> {formatDateTime()}
                </p>
                <p>
                  <strong>Data Freshness:</strong> Real-time
                </p>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
